import { ColumnType, ILookupTypeOptions } from '@treelab/panda';
import { isAbsentCellValue } from './app';

// @TODO update panda types
interface ILookupTypeOptionsV2 extends ILookupTypeOptions {
  lookupTypeOptions: any;
}
/**
 * Assists with taking raw, nested lookup value from backend, and creating a flattened
 * array of them.
 */
export const getFlattenedLookupValue = (value: any[]) => {
  if (!value) return [];
  return getFlattenedData(value, 'lookup').filter((v: any) => v.type);
};

/** Helpers for recursion mapping */
const flattenByKey = (value: any, key: string) => {
  if (!value[key]) return value;

  return value[key].reduce((accum: any[], v: any) => accum.concat(flattenByKey(v, key)), []);
};

const getFlattenedData = (value: any[], key: string) => {
  return value.reduce((accum, v) => accum.concat(flattenByKey(v, key)), []);
};

/**
 * Assists with reducing the flattened lookup value and grabbing the value based on field type. For example,
 * text lookup value will result in a comma seperated joined string. Attachments will be an array of attachments. etc.
 */
export const getFriendlyValuesFromFlattenedLookup = (
  values: any[],
  typeOptions: ILookupTypeOptionsV2,
) => {
  if (!values || !Array.isArray(values) || values.length === 0) return null;

  const { lookupColumnType } = typeOptions;

  switch (lookupColumnType) {
    case ColumnType.TEXT:
    case ColumnType.LONG_TEXT:
    case ColumnType.PHONE:
    case ColumnType.EMAIL:
    case ColumnType.AUTO_NUMBER:
    case ColumnType.ROLLUP:
      return values.reduce((accum: string, cell: any) => {
        if (isAbsentCellValue(cell)) return accum;

        return accum === '' ? cell : `${accum}, ${cell}`;
      }, '');
    // Number & DateTime will be parsed into an array, and formatted at the render level
    case ColumnType.FORMULA:
    case ColumnType.COLLABORATOR:
    case ColumnType.DATETIME:
    case ColumnType.RECORD_REFERENCE:
    case ColumnType.SUBTABLE:
    case ColumnType.MULTI_SELECT:
    case ColumnType.STATUS:
    case ColumnType.SELECT:
    case ColumnType.RATING:
    case ColumnType.MULTI_ATTACHMENT:
    case ColumnType.CREATED_AT:
    case ColumnType.CREATED_BY:
      const accumSelect = values.reduce((accum: string[], cell: any) => {
        if (isAbsentCellValue(cell)) return accum;

        return accum.concat(cell);
      }, []);
      return accumSelect;
    case ColumnType.CURRENCY:
    case ColumnType.NUMBER:
      const accumNum = values.reduce((accum: number[], cell: any) => {
        if (isNaN(cell)) return accum;

        return accum.concat(cell);
      }, []);
      return accumNum;
    case ColumnType.CHECKBOX:
      return values.reduce((accum: string, cell: any) => {
        if (isAbsentCellValue(cell)) return accum;

        return accum.concat(cell);
      }, []);
  }

  return null;
};

export const convertLookupTypeOptions = (typeOptions: ILookupTypeOptionsV2) => {
  /**
   * This is nasty nasty stuff, not with this function, but with where `type` is adapted.
   *
   * Query returns data like `{ id: string, name: string, type: ColumnType, typeOptions: {} }`,
   * However! because AG-Grid can not take `type` property in the colDef, we choose to adapt with `adaptColumn` function
   * and put the `type` inside of `typeOptions`. (We should instead, just call it `fieldType`).
   *
   * Second, a Lookup type options from subscription looks like this.
   * `{ id: string, type: ColumnType, typeOptions: { fieldType: LookupColumnType }}`. We called it `fieldType` inside
   * just so that when we put it through `adaptColumn` it will not replace `fieldType` with `type`.
   */
  switch (typeOptions.lookupColumnType) {
    case ColumnType.STATUS:
      return {
        type: typeOptions.lookupColumnType,
        statuses: typeOptions.lookupTypeOptions.statuses,
      };
    case ColumnType.SELECT:
    case ColumnType.MULTI_SELECT:
      return {
        type: typeOptions.lookupColumnType,
        options: typeOptions.lookupTypeOptions.options,
      };
    case ColumnType.COLLABORATOR:
    case ColumnType.CREATED_BY:
      return {
        type: typeOptions.lookupColumnType,
        // TODO: include these after backend implementation of lookups for COLLABORATOR
        // useMultiple: typeOptions.lookupUseMultiple,
        // useGroup: typeOptions.lookupUseGroup,
        // useNotify: typeOptions.lookupUseNotify,
      };
    case ColumnType.DATETIME:
    case ColumnType.CREATED_AT:
      return {
        type: typeOptions.lookupColumnType,
        dateFormat: typeOptions.lookupTypeOptions.dateFormat,
        timeFormat: typeOptions.lookupTypeOptions.timeFormat,
        useGMT: typeOptions.lookupTypeOptions.useGMT,
        includeTime: typeOptions.lookupTypeOptions.includeTime,
      };
    case ColumnType.NUMBER:
      return {
        type: typeOptions.lookupColumnType,
        precision: typeOptions.lookupTypeOptions.precision,
        defaultNumber: typeOptions.lookupTypeOptions.defaultNumber,
      };
    case ColumnType.CURRENCY:
      return {
        type: typeOptions.lookupColumnType,
        precision: typeOptions.lookupTypeOptions.precision,
        currencyFormat: typeOptions.lookupTypeOptions.currencyFormat,
      };
    case ColumnType.RECORD_REFERENCE:
    case ColumnType.SUBTABLE:
      return {
        type: typeOptions.lookupColumnType,
        foreignCoreId: typeOptions.lookupForeignCoreId,
        foreignTableId: typeOptions.lookupForeignTableId,
        foreignWorkspaceId: typeOptions.lookupForeignWorkspaceId,
      };
    case ColumnType.FORMULA:
      return {
        type: typeOptions.lookupColumnType,
        resultType: typeOptions.lookupTypeOptions.resultType,
        numberFormat: typeOptions.lookupTypeOptions.numberFormat,
        precision: typeOptions.lookupTypeOptions.precision,
        dateFormat: typeOptions.lookupTypeOptions.dateFormat,
        includeTime: typeOptions.lookupTypeOptions.includeTime,
        timeFormat: typeOptions.lookupTypeOptions.timeFormat,
        currencyFormat: typeOptions.lookupTypeOptions.currencyFormat,
      };
    // TODO: Change this to ColumnType.RATING so we can actual render the
    // styles, instead of just numbers.
    // @ts-ignore
    case ColumnType.LOOKUP:
      return {
        type: typeOptions.lookupColumnType,
        ratingMax: typeOptions.lookupRatingMax,
        ratingStyle: typeOptions.lookupRatingStyle,
      };
    default:
      return {
        type: typeOptions.lookupColumnType,
      };
  }
};

export const getTypeOptions = (typeOptions: any) =>
  typeOptions.type === ColumnType.LOOKUP
    ? convertLookupTypeOptions(typeOptions as ILookupTypeOptionsV2)
    : typeOptions;
